import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@mui/styles'
import {IconButton} from '@mui/material'
import {v4 as uuid} from 'uuid'
import {spaceMedium} from '../assets/styles/spacing'
import {rxrBlackColor, rxrPrimaryColor} from '../assets/styles/color'
import RXRIcon from './RXRIcon'

const useStyles = makeStyles(theme => ({
  inputContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: spaceMedium,
    width: '100%',
    gap: theme.spacing(3),

    '&:last-child': {
      marginBottom: 0,
    },
  },
  iconPlaceholder: {
    width: 45,
    height: 56,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
  },
}))

function getId(obj) {
  return obj.id || obj.rowId
}

function isObjectComplete(obj) {
  return !Object.values(obj).some(o => o === null || o === '')
}

function GenericManyInput(props) {
  const classes = useStyles()
  const {value} = props
  const [newModel, setNewModel] = useState(props.emptyModel)

  const validator = props.validator || isObjectComplete

  const handleExistingChange = obj => {
    if (!props.showPlusIcon && !validator(obj)) {
      return
    }

    console.log('handleExistingChange', obj)

    let valueObj = value.find(a => getId(a) === getId(obj))
    Object.keys(props.emptyModel).forEach(key => {
      if (key !== 'rowId') {
        valueObj[key] = obj[key]
      }
    })

    props.onChange([...value])
  }

  const handleClickPlus = () => {
    return createNewObject(newModel)
  }

  const handleNewChange = obj => {
    // then we never automatically create a new row
    if (validator(obj)) {
      createNewObject(obj)
    } else {
      console.log('Update new model')
      // just update the new model
      setNewModel(obj)
      props.onChange(value, obj)
    }
  }

  const createNewObject = obj => {
    console.log('createNewObject')
    let newObj = {
      ...obj,
      rowId: uuid(),
    }

    // we add it to the list
    props.onChange([...value, newObj])
    // and reset our New input object
    setNewModel(props.emptyModel)

    // call on Add if it's present
    if (typeof props.onAdd === 'function') {
      props.onAdd(newObj)
    }
  }

  const handleRemove = obj => {
    props.onChange(value.filter(a => getId(a) !== getId(obj)))

    // call on Remove if it's present
    if (typeof props.onRemove === 'function') {
      props.onRemove(obj)
    }
  }

  const SingleInputComponent = props.singleInputComponent

  return (
    <React.Fragment>
      {value.map((field, index) => {
        const iconOverride = typeof props.iconOverride === 'function' ? props.iconOverride(field, index) : null
        return (
          <div key={getId(field) || index} className={[classes.inputContainer, props.inputContainerClassName].join(' ')}>
            <SingleInputComponent
              {...props.singleInputProps}
              isRequired={index === 0}
              index={index}
              value={field}
              onChange={handleExistingChange}
              error={Array.isArray(props.erroredEntries) ? props.erroredEntries[index] : null}
              isDisabled={Array.isArray(props.disabledEntries) ? props.disabledEntries[index] : false}
            />
            {iconOverride ? (
              <div className={classes.iconPlaceholder}>{iconOverride}</div>
            ) : (
              <IconButton style={{flexShrink: 0}} component="span" aria-label="remove" onClick={() => handleRemove(field)} size="large">
                <RXRIcon icon={RXRIcon.TRASH} color={rxrBlackColor} size={RXRIcon.SIZE_LARGE} />
              </IconButton>
            )}
          </div>
        )
      })}
      {!props.hideNewInput && (
        <div className={[classes.inputContainer, props.inputContainerClassName].join(' ')}>
          <SingleInputComponent
            key={value.length}
            index={value.length}
            {...props.singleInputProps}
            value={newModel}
            isRequired={value.length === 0}
            onChange={handleNewChange}
            error={props.error}
          />
          {props.showPlusIcon ? (
            <IconButton style={{flexShrink: 0}} component="span" aria-label="remove" onClick={handleClickPlus} size="large">
              <RXRIcon icon={RXRIcon.PLUS} color={rxrPrimaryColor} size={RXRIcon.SIZE_LARGE} />
            </IconButton>
          ) : (
            <div className={classes.iconPlaceholder} />
          )}
        </div>
      )}
    </React.Fragment>
  )
}

GenericManyInput.propTypes = {
  onChange: PropTypes.func.isRequired, // returns the array of objects
  value: PropTypes.array.isRequired, // an array of objects
  emptyModel: PropTypes.object.isRequired, // an object that represents an empty state of the object we're modifying
  singleInputComponent: PropTypes.func.isRequired, // a component that represents a single input, must receive `value`, `onChange`, `isRequired`, `error`, and `index`, `isDisabled` props
  singleInputProps: PropTypes.object,
  inputContainerClassName: PropTypes.string, // optional className for the input container
  showPlusIcon: PropTypes.bool, // optional, if true, will show the add new icon at the end of the list
  getSingleInputIcon: PropTypes.func, // optional function to get an icon for the single input, if not provided, it will use the default icon
  onRemove: PropTypes.func, // optional callback to handle Removals separate
  onAdd: PropTypes.func, // optional callback to handle New elements separate
  disabledEntries: PropTypes.array, // array of booleans, should match the length of the value array, if true, the input will be disabled
  erroredEntries: PropTypes.array, // array of errors for each input, should match the length of the value array
  error: PropTypes.bool, // if true, the new input box itself will show error
  hideNewInput: PropTypes.bool, // optional, if true, will hide the new input row
  validator: PropTypes.func, // optional function to validate the input, should return true if valid, false otherwise
}

/*
NOTE:
If `showPlusIcon` is false, the value passed to the onChange function will only ever include "completed" objects
If `showPlusIcon` is true, it's likely that multiple objects in the value array will be incomplete. This is because
we allow the user to click "+" many times to queue up a bunch of empty inputs.
 */

export default GenericManyInput
